import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { calculateOrderItemTotal, formatPrice, getCurrency, getPriceField } from '../../utils/pricingCalculations';

const RecurringOrderModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  order = null,
  clientes = [],
  productos = [],
  temporada = null
}) => {
  const [formData, setFormData] = useState({
    cliente_id: '',
    temporada: temporada?.year || new Date().getFullYear(),
    semana_inicio: 1,
    semana_fin: 52,
    tipo: 'Exportación',
    detalles: []
  });

  const [errors, setErrors] = useState({});
  const [clientesFiltrados, setClientesFiltrados] = useState([]);

  // Filtrar clientes según el tipo seleccionado
  useEffect(() => {
    if (formData.tipo === 'Exportación') {
      setClientesFiltrados(clientes.filter(c => c.pais === 'USA' || c.pais === 'Canada'));
    } else {
      setClientesFiltrados(clientes.filter(c => c.pais === 'CL'));
    }
  }, [formData.tipo, clientes]);

  // Reset del formulario cuando el modal se abre/cierra
  useEffect(() => {
    if (!isOpen) {
      // Limpiar errores cuando se cierra el modal
      setErrors({});
      return;
    }

    if (order) {
      // Modo edición
      setFormData({
        cliente_id: order.cliente_id ? String(order.cliente_id) : '',
        temporada: order.temporada || temporada?.year || new Date().getFullYear(),
        semana_inicio: order.semana_inicio || temporada?.season?.semana_inicio || 1,
        semana_fin: order.semana_fin || temporada?.season?.semana_fin || 52,
        tipo: order.tipo || 'Exportación',
        detalles: order.detalles || []
      });
    } else {
      // Modo creación - usar límites de la temporada
      const semanaInicio = temporada?.season?.semana_inicio || 1;
      const semanaFin = temporada?.season?.semana_fin || 52;
      
      setFormData({
        cliente_id: '',
        temporada: temporada?.year || new Date().getFullYear(),
        semana_inicio: semanaInicio,
        semana_fin: semanaFin,
        tipo: 'Exportación',
        detalles: [{ producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
      });
    }
  }, [isOpen, order, temporada]);

  const handleChange = (field, value) => {
    // Si cambia el tipo, resetear el cliente seleccionado y actualizar precios de productos
    if (field === 'tipo') {
      // Actualizar precios de todos los productos existentes según el nuevo tipo
      const updatedDetalles = formData.detalles.map(detalle => {
        if (detalle.producto_id) {
          const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
          if (producto) {
            const priceField = getPriceField(value); // Usar el nuevo tipo
            const precio = producto[priceField];
            return {
              ...detalle,
              precio_unitario: precio ? parseFloat(precio) : 0
            };
          }
        }
        return detalle;
      });
      
      setFormData(prev => ({ 
        ...prev, 
        [field]: value, 
        cliente_id: '',
        detalles: updatedDetalles
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDetalleChange = (index, field, value) => {
    const newDetalles = [...formData.detalles];
    newDetalles[index] = { ...newDetalles[index], [field]: value };
    
    // Si cambia el producto, actualizar el precio unitario automáticamente según el tipo de pedido
    if (field === 'producto_id') {
      const producto = productos.find(p => p.id === parseInt(value));
      if (producto) {
        // Usar precio_unitario para Nacional, precio_fob_mia para Exportación
        const priceField = getPriceField(formData.tipo);
        const precio = producto[priceField];
        if (precio) {
          newDetalles[index].precio_unitario = parseFloat(precio);
        }
      }
    }
    
    setFormData(prev => ({ ...prev, detalles: newDetalles }));
  };

  const addDetalle = () => {
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, { producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
    }));
  };

  const removeDetalle = (index) => {
    if (formData.detalles.length === 1) {
      alert('Debe haber al menos un producto en el pedido');
      return;
    }
    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
  };

  const calculateLineTotal = (detalle) => {
    if (!detalle.producto_id) return 0;
    
    const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
    if (!producto) return 0;
    
    const cantidad = parseFloat(detalle.cantidad_cajas) || 0;
    const precioManual = parseFloat(detalle.precio_unitario) || 0;
    
    if (cantidad <= 0 || precioManual <= 0) return 0;
    
    try {
      // Aplicar fórmulas centralizadas pero con el precio modificado manualmente:
      // Exportación: precio_manual × ramos_por_caja × cajas
      // Nacional: precio_manual × ramos_por_caja × tallos_por_ramo × cajas
      const ramosPorCaja = Number(producto.ramos_por_caja || producto.bunches_box) || 0;
      
      if (ramosPorCaja <= 0) return 0;
      
      if (formData.tipo === 'Exportación') {
        return precioManual * ramosPorCaja * cantidad;
      } else {
        const tallosPorRamo = Number(producto.tallos_por_ramo || producto.stems_bunches) || 0;
        if (tallosPorRamo <= 0) return 0;
        return precioManual * ramosPorCaja * tallosPorRamo * cantidad;
      }
    } catch (error) {
      console.error('Error calculating line total:', error);
      return 0;
    }
  };

  const calculateOrderTotal = () => {
    return formData.detalles.reduce((sum, detalle) => {
      return sum + calculateLineTotal(detalle);
    }, 0);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.cliente_id) {
      newErrors.cliente_id = 'Seleccione un cliente';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Seleccione un tipo de pedido';
    }

    // Obtener límites de la temporada
    const minWeek = temporada?.season?.semana_inicio || 1;
    const maxWeek = temporada?.season?.semana_fin || 53;

    if (formData.semana_inicio < minWeek || formData.semana_inicio > maxWeek) {
      newErrors.semana_inicio = `Semana inicio debe estar entre ${minWeek} y ${maxWeek}`;
    }

    if (formData.semana_fin < minWeek || formData.semana_fin > maxWeek) {
      newErrors.semana_fin = `Semana fin debe estar entre ${minWeek} y ${maxWeek}`;
    }

    if (formData.semana_inicio > formData.semana_fin) {
      newErrors.semana_fin = 'La semana fin debe ser mayor o igual a la semana inicio';
    }

    if (formData.detalles.length === 0) {
      newErrors.detalles = 'Agregue al menos un producto';
    }

    formData.detalles.forEach((detalle, index) => {
      if (!detalle.producto_id) {
        newErrors[`detalle_${index}_producto`] = 'Seleccione un producto';
      }
      if (!detalle.cantidad_cajas || detalle.cantidad_cajas <= 0) {
        newErrors[`detalle_${index}_cantidad`] = 'Cantidad debe ser mayor a 0';
      }
      if (!detalle.precio_unitario || detalle.precio_unitario <= 0) {
        newErrors[`detalle_${index}_precio`] = 'Precio debe ser mayor a 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Preparar datos para enviar
    const dataToSave = {
      ...formData,
      cliente_id: parseInt(formData.cliente_id),
      detalles: formData.detalles.map(detalle => ({
        producto_id: parseInt(detalle.producto_id),
        cantidad_cajas: parseInt(detalle.cantidad_cajas),
        precio_unitario: parseFloat(detalle.precio_unitario),
        precio_total: calculateLineTotal(detalle)
      }))
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {order ? 'Editar Pedido Recurrente' : 'Nuevo Pedido Recurrente'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Información General */}
            <div className="form-section">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Tipo de Pedido</label>
                  <select
                    className={`form-select ${errors.tipo ? 'error' : ''}`}
                    value={formData.tipo}
                    onChange={(e) => handleChange('tipo', e.target.value)}
                  >
                    <option value="Exportación">Exportación (USA/Canadá)</option>
                    <option value="Nacional">Nacional (Chile)</option>
                  </select>
                  {errors.tipo && <span className="error-message">{errors.tipo}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label required">Cliente</label>
                  <select
                    className={`form-select ${errors.cliente_id ? 'error' : ''}`}
                    value={formData.cliente_id}
                    onChange={(e) => handleChange('cliente_id', e.target.value)}
                  >
                    <option value="">Seleccione un cliente</option>
                    {clientesFiltrados.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre} - {cliente.pais}
                      </option>
                    ))}
                  </select>
                  {errors.cliente_id && <span className="error-message">{errors.cliente_id}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label required">Temporada</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.temporada}
                    onChange={(e) => handleChange('temporada', parseInt(e.target.value))}
                    min="2020"
                    max="2050"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    Semana Inicio
                    {temporada?.season && (
                      <span className="label-hint">
                        (Rango: {temporada.season.semana_inicio}-{temporada.season.semana_fin})
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    className={`form-input ${errors.semana_inicio ? 'error' : ''}`}
                    value={formData.semana_inicio}
                    onChange={(e) => handleChange('semana_inicio', parseInt(e.target.value))}
                    min={temporada?.season?.semana_inicio || 1}
                    max={temporada?.season?.semana_fin || 53}
                  />
                  {errors.semana_inicio && <span className="error-message">{errors.semana_inicio}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label required">
                    Semana Fin
                    {temporada?.season && (
                      <span className="label-hint">
                        (Rango: {temporada.season.semana_inicio}-{temporada.season.semana_fin})
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    className={`form-input ${errors.semana_fin ? 'error' : ''}`}
                    value={formData.semana_fin}
                    onChange={(e) => handleChange('semana_fin', parseInt(e.target.value))}
                    min={temporada?.season?.semana_inicio || 1}
                    max={temporada?.season?.semana_fin || 53}
                  />
                  {errors.semana_fin && <span className="error-message">{errors.semana_fin}</span>}
                </div>
              </div>
            </div>

            {/* Productos */}
            <div className="form-section">
              <div className="form-section-header">
                <h3 className="form-section-title">Productos</h3>
                <button type="button" className="btn btn-sm btn-primary" onClick={addDetalle}>
                  <Plus size={16} />
                  Agregar Producto
                </button>
              </div>

              {errors.detalles && (
                <div className="alert alert-error mb-4">
                  {errors.detalles}
                </div>
              )}

              <div className="products-table">
                <div className="products-table-header">
                  <div className="table-col-number">#</div>
                  <div className="table-col-product">Producto</div>
                  <div className="table-col-cajas">Cajas</div>
                  <div className="table-col-precio">Precio Unit. ({getCurrency(formData.tipo)})</div>
                  <div className="table-col-total">Total</div>
                  <div className="table-col-actions"></div>
                </div>
                
                <div className="products-table-body">
                  {formData.detalles.map((detalle, index) => (
                    <div key={index} className="product-row">
                      <div className="table-col-number">
                        <div className="product-line-number">{index + 1}</div>
                      </div>
                      
                      <div className="table-col-product">
                        <select
                          className={`form-select ${errors[`detalle_${index}_producto`] ? 'error' : ''}`}
                          value={detalle.producto_id}
                          onChange={(e) => handleDetalleChange(index, 'producto_id', e.target.value)}
                        >
                          <option value="">Seleccione un producto</option>
                          {productos.filter(p => p.activo !== false).map(producto => (
                            <option key={producto.id} value={producto.id}>
                              {producto.nombre} ({producto.code})
                            </option>
                          ))}
                        </select>
                        {errors[`detalle_${index}_producto`] && (
                          <span className="error-message">{errors[`detalle_${index}_producto`]}</span>
                        )}
                      </div>

                      <div className="table-col-cajas">
                        <input
                          type="number"
                          className={`form-input ${errors[`detalle_${index}_cantidad`] ? 'error' : ''}`}
                          value={detalle.cantidad_cajas}
                          onChange={(e) => handleDetalleChange(index, 'cantidad_cajas', e.target.value)}
                          min="1"
                        />
                        {errors[`detalle_${index}_cantidad`] && (
                          <span className="error-message">{errors[`detalle_${index}_cantidad`]}</span>
                        )}
                      </div>

                      <div className="table-col-precio">
                        <input
                          type="number"
                          className={`form-input ${errors[`detalle_${index}_precio`] ? 'error' : ''}`}
                          value={detalle.precio_unitario}
                          onChange={(e) => handleDetalleChange(index, 'precio_unitario', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                        {errors[`detalle_${index}_precio`] && (
                          <span className="error-message">{errors[`detalle_${index}_precio`]}</span>
                        )}
                      </div>

                      <div className="table-col-total">
                        <div className="form-input-static">
                          {formatPrice(calculateLineTotal(detalle), getCurrency(formData.tipo))}
                        </div>
                      </div>

                      <div className="table-col-actions">
                        <button
                          type="button"
                          className="product-line-remove"
                          onClick={() => removeDetalle(index)}
                          disabled={formData.detalles.length === 1}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="order-total">
                <div className="order-total-label">Total del Pedido:</div>
                <div className="order-total-value">
                  {formatPrice(calculateOrderTotal(), getCurrency(formData.tipo))}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {order ? 'Actualizar' : 'Crear'} Pedido Recurrente
            </button>
          </div>
        </form>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--spacing-4);
          }

          .modal-container {
            background: white;
            border-radius: var(--radius-lg);
            width: 100%;
            max-width: 900px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-6);
            border-bottom: 1px solid var(--gray-200);
            background: linear-gradient(135deg, var(--secondary-green) 0%, #1a4d2e 100%);
          }

          .modal-title {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: white;
            margin: 0;
          }

          .modal-close {
            padding: var(--spacing-2);
            border: none;
            background: rgba(255, 255, 255, 0.2);
            cursor: pointer;
            color: white;
            border-radius: var(--radius-md);
            transition: all 0.2s;
          }

          .modal-close:hover {
            background-color: rgba(255, 255, 255, 0.3);
          }

          .modal-body {
            padding: var(--spacing-5);
            overflow-y: auto;
            flex: 1;
            max-height: calc(90vh - 180px);
          }

          .form-section {
            margin-bottom: var(--spacing-6);
          }

          .form-section:first-of-type {
            margin-bottom: var(--spacing-4);
          }

          .form-section-title {
            font-size: var(--font-size-base);
            font-weight: 600;
            color: var(--gray-900);
            margin: 0;
          }

          .form-section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: var(--spacing-3);
          }

          .form-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: var(--spacing-3);
          }

          .form-grid .form-group:nth-child(1),
          .form-grid .form-group:nth-child(2) {
            grid-column: span 3;
          }

          @media (min-width: 769px) {
            .form-grid .form-group:nth-child(1),
            .form-grid .form-group:nth-child(2) {
              grid-column: span 1;
            }
            
            .form-grid .form-group:nth-child(2) {
              grid-column: span 2;
            }
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
          }

          .form-group.flex-1 {
            flex: 1;
          }

          .form-label {
            font-size: 11px;
            font-weight: 500;
            color: var(--gray-700);
            line-height: 1.2;
          }

          .form-label.required::after {
            content: ' *';
            color: var(--error);
          }

          .label-hint {
            font-size: var(--font-size-xs);
            font-weight: 400;
            color: var(--gray-500);
            margin-left: var(--spacing-2);
          }

          .form-input,
          .form-select {
            padding: 6px var(--spacing-2);
            border: 1px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            transition: border-color 0.2s;
            height: 32px;
            box-sizing: border-box;
          }

          .form-input:focus,
          .form-select:focus {
            outline: none;
            border-color: var(--primary-blue);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-input.error,
          .form-select.error {
            border-color: var(--error);
          }

          .form-input:disabled {
            background-color: var(--gray-100);
            cursor: not-allowed;
          }

          .form-input-static {
            padding: 6px var(--spacing-2);
            background-color: white;
            border: 1px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            font-weight: 400;
            color: var(--gray-900);
            display: flex;
            align-items: center;
            height: 32px;
            line-height: 1.5;
            box-sizing: border-box;
            font-family: inherit;
          }

          .error-message {
            font-size: var(--font-size-sm);
            color: var(--error);
            margin-top: var(--spacing-1);
          }

          .alert {
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
          }

          .alert-error {
            background-color: #fef2f2;
            border: 1px solid #fecaca;
            color: #dc2626;
          }

          .mb-4 {
            margin-bottom: var(--spacing-4);
          }

          .products-table {
            border: 1px solid var(--gray-200);
            border-radius: var(--radius-md);
            overflow: hidden;
          }

          .products-table-header {
            display: grid;
            grid-template-columns: 40px 1fr 90px 110px 110px 50px;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-3);
            background-color: var(--gray-100);
            border-bottom: 2px solid var(--gray-300);
            font-size: 11px;
            font-weight: 600;
            color: var(--gray-700);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .products-table-body {
            display: flex;
            flex-direction: column;
          }

          .product-row {
            display: grid;
            grid-template-columns: 40px 1fr 90px 110px 110px 50px;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-3);
            border-bottom: 1px solid var(--gray-200);
            background-color: white;
            align-items: center;
          }

          .product-row:last-child {
            border-bottom: none;
          }

          .product-row:hover {
            background-color: var(--gray-50);
          }

          .table-col-number {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .product-line-number {
            width: 24px;
            height: 24px;
            background-color: var(--secondary-green);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: var(--font-size-xs);
          }

          .table-col-product {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .table-col-cajas,
          .table-col-precio,
          .table-col-total {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .table-col-actions {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .product-line-remove {
            padding: 6px;
            border: none;
            background-color: transparent;
            color: var(--error);
            cursor: pointer;
            border-radius: var(--radius-md);
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .product-line-remove:hover:not(:disabled) {
            background-color: var(--error);
            color: white;
          }

          .product-line-remove:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .order-total {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: var(--spacing-4);
            padding: var(--spacing-4);
            background-color: var(--gray-100);
            border-radius: var(--radius-lg);
            margin-top: var(--spacing-4);
          }

          .order-total-label {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-700);
          }

          .order-total-value {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: var(--secondary-green);
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-3);
            padding: var(--spacing-6);
            border-top: 1px solid var(--gray-200);
            background-color: var(--gray-50);
          }

          @media (max-width: 768px) {
            .modal-container {
              max-width: 100%;
              max-height: 100vh;
              border-radius: 0;
            }

            .form-grid {
              grid-template-columns: 1fr;
            }

            .products-table-header {
              display: none;
            }

            .product-row {
              grid-template-columns: 30px 1fr 40px;
              grid-template-areas:
                "number product actions"
                "number cajas actions"
                "number precio actions"
                "number total actions";
              gap: var(--spacing-1);
              padding: var(--spacing-3);
            }

            .table-col-number {
              grid-area: number;
            }

            .table-col-product {
              grid-area: product;
            }

            .table-col-cajas {
              grid-area: cajas;
            }

            .table-col-cajas::before {
              content: "Cajas: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-precio {
              grid-area: precio;
            }

            .table-col-precio::before {
              content: "Precio: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-total {
              grid-area: total;
            }

            .table-col-total::before {
              content: "Total: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-actions {
              grid-area: actions;
              align-self: start;
            }

            .table-col-cajas,
            .table-col-precio,
            .table-col-total {
              flex-direction: row;
              align-items: center;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default RecurringOrderModal;
